import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, FileText, CheckCircle2, AlertTriangle, XCircle, Wrench, Package,
} from "lucide-react";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const CFG = {
  good: { icon: CheckCircle2, cls: "text-emerald-400", label: "Good" },
  attention: { icon: AlertTriangle, cls: "text-amber-400", label: "Needs Attention" },
  critical: { icon: XCircle, cls: "text-rose-400", label: "Critical" },
};

let _seq = 0;
const uid = () => `est_${Date.now()}_${_seq++}`;

export default function ReportToEstimate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const laborRate = user?.labor_rate || 100;

  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [taxRate, setTaxRate] = useState(0);
  const [lines, setLines] = useState([]); // {_key, from, description, quantity, unit_price, kind, include}

  useEffect(() => {
    api.get(`/reports/${id}`).then(({ data }) => {
      setRep(data);
      const seed = [];
      (data.items || []).forEach((it, idx) => {
        const hasPart = it.part_name || Number(it.part_cost || 0) > 0;
        const hasLabor = Number(it.labor_hours || 0) > 0;
        if (hasPart) {
          seed.push({ _key: uid(), from: idx, description: it.part_name || it.label, quantity: 1, unit_price: Number(it.part_cost || 0), kind: "part", include: it.status !== "good" });
        }
        if (hasLabor) {
          seed.push({ _key: uid(), from: idx, description: `${it.label} (labor)`, quantity: Number(it.labor_hours || 0), unit_price: laborRate, kind: "labor", include: it.status !== "good" });
        }
      });
      setLines(seed);
    }).catch(() => toast.error("Report not found")).finally(() => setLoading(false));
  }, [id, laborRate]);

  const toggle = (key) => setLines((ls) => ls.map((l) => (l._key === key ? { ...l, include: !l.include } : l)));
  const update = (key, k, v) => setLines((ls) => ls.map((l) => (l._key === key ? { ...l, [k]: v } : l)));

  const included = useMemo(() => lines.filter((l) => l.include), [lines]);
  const totals = useMemo(() => {
    const subtotal = included.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);
    const tax = subtotal * (Number(taxRate || 0) / 100);
    return { subtotal, tax, total: subtotal + tax };
  }, [included, taxRate]);

  const createEstimate = async () => {
    if (!rep) return;
    if (included.length === 0) return toast.error("Select at least one item for the estimate");
    setSaving(true);
    const payload = {
      doc_type: "estimate",
      customer_name: rep.customer_name || "",
      customer_business_name: rep.customer_business_name || "",
      customer_business_address: rep.customer_business_address || "",
      vehicle_make: rep.vehicle_make || "", vehicle_model: rep.vehicle_model || "",
      vehicle_year: rep.vehicle_year || "", vehicle_vin: rep.vehicle_vin || "",
      tax_rate: Number(taxRate || 0),
      notes: `Generated from Vehicle Health Report ${rep.number || ""}`.trim(),
      status: "pending",
      items: included.map((l) => ({ description: l.description, quantity: Number(l.quantity || 0), unit_price: Number(l.unit_price || 0), kind: l.kind })),
    };
    try {
      const { data } = await api.post("/invoices", payload);
      toast.success("Estimate created from report");
      navigate(`/invoices/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create estimate");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Layout title="Loading…"><div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div></Layout>;
  if (!rep) return <Layout title="Not found"><p className="text-slate-400">Report not found.</p></Layout>;

  const vehicle = [rep.vehicle_year, rep.vehicle_make, rep.vehicle_model].filter(Boolean).join(" ");

  return (
    <Layout
      title="Build Estimate from Report"
      action={
        <div className="flex gap-2">
          <Button variant="outline" data-testid="back-to-report" onClick={() => navigate(`/reports/${id}`)} className="bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800"><ArrowLeft className="h-4 w-4 mr-1.5" /> Report</Button>
          <Button data-testid="create-estimate-button" onClick={createEstimate} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />} Create Estimate</Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-up">
        {/* LEFT: Inspection checklist (read-only) */}
        <section className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid="report-side">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-head font-bold text-slate-100">Inspection · {rep.number}</h3>
            <span className="text-xs text-slate-500">{vehicle || "—"}</span>
          </div>
          <p className="text-xs text-slate-500 mb-4">{rep.customer_name}</p>
          <div className="space-y-1.5">
            {(rep.items || []).map((it, i) => {
              const cfg = CFG[it.status] || CFG.good;
              const Icon = cfg.icon;
              const cost = Number(it.part_cost || 0) + Number(it.labor_hours || 0) * laborRate;
              return (
                <div key={i} data-testid={`report-item-${i}`} className="flex items-start gap-3 py-2 border-b border-slate-800/60">
                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${cfg.cls}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-100">{it.label} <span className="text-slate-600 text-xs">· {it.system}</span></p>
                    {it.note && <p className="text-xs text-slate-500">{it.note}</p>}
                    {(it.part_name || it.labor_hours) && (
                      <p className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5"><Package className="h-3 w-3 text-slate-500" />{it.part_name || "Labor"}{it.labor_hours ? ` · ${it.labor_hours}h` : ""}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`text-xs font-semibold ${cfg.cls}`}>{cfg.label}</span>
                    {cost > 0 && <p className="text-xs text-slate-300 tabular-nums mt-0.5">{money(cost)}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* RIGHT: Estimate builder */}
        <section className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid="estimate-side">
          <h3 className="font-head font-bold text-slate-100 mb-1">Estimate</h3>
          <p className="text-xs text-slate-500 mb-4">Toggle items to include. Parts and labor are pulled from the report.</p>

          {lines.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm border border-dashed border-slate-800 rounded-lg" data-testid="no-priced-items">
              No parts or labor were priced on this report.<br />Add them in the report editor first.
            </div>
          ) : (
            <div className="space-y-2">
              {lines.map((l) => (
                <div key={l._key} data-testid={`estimate-line-${l._key}`} className={`grid grid-cols-12 gap-2 items-center rounded-lg border p-2 transition-colors ${l.include ? "border-slate-700 bg-slate-950/60" : "border-slate-800/40 bg-slate-950/20 opacity-50"}`}>
                  <div className="col-span-1 flex justify-center">
                    <Checkbox checked={l.include} onCheckedChange={() => toggle(l._key)} data-testid={`toggle-line-${l._key}`} className="border-slate-600 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" />
                  </div>
                  <div className="col-span-6 min-w-0">
                    <Input value={l.description} onChange={(e) => update(l._key, "description", e.target.value)} className="h-9 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
                    <span className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1 mt-0.5">{l.kind === "labor" ? <Wrench className="h-3 w-3" /> : <Package className="h-3 w-3" />}{l.kind}</span>
                  </div>
                  <Input type="number" step="0.1" value={l.quantity} onChange={(e) => update(l._key, "quantity", e.target.value)} className="col-span-2 h-9 bg-slate-950 border-slate-700 text-right focus-visible:ring-amber-500/50" />
                  <div className="col-span-3 relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                    <Input type="number" step="0.01" value={l.unit_price} onChange={(e) => update(l._key, "unit_price", e.target.value)} className="pl-5 h-9 bg-slate-950 border-slate-700 text-right focus-visible:ring-amber-500/50" />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 pt-4 border-t border-slate-800 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-slate-300 text-xs">Tax rate (%)</Label>
              <Input type="number" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} data-testid="estimate-tax-rate" className="h-9 w-28 bg-slate-950 border-slate-700 text-right focus-visible:ring-amber-500/50" />
            </div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-400"><span>Subtotal ({included.length} items)</span><span className="tabular-nums text-slate-200" data-testid="estimate-subtotal">{money(totals.subtotal)}</span></div>
              <div className="flex justify-between text-slate-400"><span>Tax ({taxRate || 0}%)</span><span className="tabular-nums text-slate-200">{money(totals.tax)}</span></div>
              <div className="flex justify-between items-center pt-2 border-t border-slate-800">
                <span className="font-head font-bold text-slate-100">Total</span>
                <span className="font-head font-black text-2xl text-amber-400 tabular-nums" data-testid="estimate-total">{money(totals.total)}</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
